import React, { useCallback, useMemo, useState } from "react";
import { Button, DatePicker, Form, Input, Select } from "antd";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";
import { Nullable } from "../../../../models/Common";
import {
  FleetCardAssignment,
  FleetExpenseParameters,
  FleetExpenseResponseData,
  fleetExpensePaymentTypeMap,
} from "../../../../models/FleetExpenses/FleetExpensesModel";
import {
  currencyMap,
  CurrencyType,
  CurrencyTypeUppercase,
} from "../../../../constants/currency";
import { OptionMap } from "../../../../helpers/optionMap";
import { addMessage, callMessages } from "../../../../helpers/flashMessage";
import useFleetExpensesDrivers from "../../../../hooks/fleetExpenses/useFleetExpensesDrivers";
import { useFleetExpensesSpendingTypes } from "../../../../hooks/fleetExpenses/useFleetExpensesSpendingTypes";
import { useFleetExpensesTransports } from "../../../../hooks/fleetExpenses/useFleetExpensesTransports";
import { formItemFactory } from "../../../formItems/FormItemFactory";
import ConditionalFormSection, {
  ConditionalFormSectionMap,
} from "../../../formItems/ConditionalFormSection";
import ExecutableModal from "../../../executableModal/ExecutableModal";
import { isFleetExpenseOfType } from "../../../tables/fleetExpenses/config";
import CardAssignmentFormSection from "./components/CardAssignmentFormSection";
import { createCardAssignmentOptions } from "./components/cardAssignmentOptions";
import DriverSelect from "./components/DriverSelect";
import {
  CARD_ASSIGNMENT_SELECT_PREFIX,
  DRIVER_SELECT_PREFIX,
} from "./constants";
import { searchByLabelProps } from "./selectSearchProps";
import {
  DetermineExpense,
  FleetExpenseFormValues,
  FleetExpenseModalProps,
} from "./types";
import validations from "./validations";

const FleetExpenseItem = formItemFactory<FleetExpenseFormValues>(validations);

const FleetExpenseModal = <Edit extends boolean = false>({
  handler,
  store,
  expense,
  edit,
  onResult,
}: FleetExpenseModalProps<Edit>): React.ReactElement => {
  const [form] = Form.useForm<FleetExpenseFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cardAssignments, setCardAssignments] = useState<
    Nullable<FleetCardAssignment[]>
  >(null);
  const [assignmentRefreshKey, setAssignmentRefreshKey] = useState<number>(0);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState<boolean>(
    false
  );
  const {
    drivers,
    isLoading: driversLoading,
    error: driversError,
  } = useFleetExpensesDrivers(store);
  const {
    transports,
    isLoading: transportsLoading,
  } = useFleetExpensesTransports(store);
  const {
    spendingTypes,
    isLoading: spendingTypesLoading,
  } = useFleetExpensesSpendingTypes(store);

  const driverOptions = useMemo<Nullable<OptionMap<string>>>(() => {
    if (!drivers) {
      return null;
    }

    const options = new OptionMap<string>(
      drivers.getOptions().reduce((result, option) => {
        result[option.value] = option.title;
        return result;
      }, {} as Record<string, string>)
    );

    if (edit && expense) {
      const expenseTyped = expense as DetermineExpense<true>;
      if (expenseTyped) {
        options.addOption(
          expenseTyped.driver.id.toString(),
          expenseTyped.driver.name
        );
      }
    }

    return options;
  }, [drivers, edit, expense]);

  const cardAssignmentOptions = useMemo<Nullable<OptionMap<string>>>(() => {
    if (cardAssignments === null || cardAssignments === undefined) {
      return null;
    }
    return createCardAssignmentOptions(cardAssignments);
  }, [cardAssignments]);

  const onShow = useCallback(() => {
    setCardAssignments(null);
    setIsLoadingAssignments(false);
    setAssignmentRefreshKey((previousKey) => previousKey + 1);

    if (edit && expense) {
      const expenseTyped = { ...expense } as DetermineExpense<true>;
      form.setFieldsValue({
        driver_id: `${DRIVER_SELECT_PREFIX}${expenseTyped.driver.id}`,
        tc_id: expenseTyped.tc.id.toString() || undefined,
        spending_type_id: expenseTyped.spendingType.id.toString(),
        amount: expenseTyped.amount,
        date: moment(expenseTyped.date, "YYYY-MM-DD"),
        currency: expenseTyped.currency.toLowerCase() as CurrencyType,
        payment_type: expenseTyped.paymentType,
        note: expenseTyped.note || "",
        card_assignment_id:
          expenseTyped.paymentType === "By Card" &&
          expenseTyped.card_assignment_id !== null &&
          expenseTyped.card_assignment_id !== undefined
            ? `${CARD_ASSIGNMENT_SELECT_PREFIX}${expenseTyped.card_assignment_id}`
            : undefined,
      });
    } else {
      const expenseTyped = expense as DetermineExpense<false>;
      form.setFieldsValue({
        driver_id:
          expenseTyped?.driver?.id !== undefined
            ? `${DRIVER_SELECT_PREFIX}${expenseTyped.driver.id}`
            : undefined,
        card_assignment_id: undefined,
        tc_id: expenseTyped?.tc.id.toString() || undefined,
        spending_type_id:
          expenseTyped && isFleetExpenseOfType(expenseTyped, "spending-type")
            ? expenseTyped.spendingType.id.toString()
            : undefined,
        amount: 0,
        date: moment(Date.now()),
        currency: "eur",
        payment_type: "By Card",
        note: "",
      });
    }
  }, [edit, expense, form]);

  const handleSubmit = useCallback(async () => {
    let values: FleetExpenseFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const {
      driver_id: selectedDriverValue,
      card_assignment_id: selectedAssignmentValue,
      ...expenseValues
    } = values;
    const driverKey = driverOptions?.parsePrefix(
      selectedDriverValue,
      DRIVER_SELECT_PREFIX
    );
    if (driverKey === null || driverKey === undefined) {
      form.setFields([
        { name: "driver_id", errors: ["Select a valid driver."] },
      ]);
      return;
    }

    const driverId = Number(driverKey);
    if (!Number.isSafeInteger(driverId)) {
      form.setFields([
        { name: "driver_id", errors: ["Select a valid driver."] },
      ]);
      return;
    }

    let cardAssignmentId: Nullable<number> = null;
    if (values.payment_type === "By Card") {
      const assignmentKey = cardAssignmentOptions?.parsePrefix(
        selectedAssignmentValue ?? "",
        CARD_ASSIGNMENT_SELECT_PREFIX
      );
      if (assignmentKey === null || assignmentKey === undefined) {
        form.setFields([
          {
            name: "card_assignment_id",
            errors: ["Select a valid card assignment."],
          },
        ]);
        return;
      }

      const parsedAssignmentId = Number(assignmentKey);
      if (!Number.isSafeInteger(parsedAssignmentId)) {
        form.setFields([
          {
            name: "card_assignment_id",
            errors: ["Select a valid card assignment."],
          },
        ]);
        return;
      }
      cardAssignmentId = parsedAssignmentId;
    }

    const parameters: FleetExpenseParameters = {
      ...expenseValues,
      driver_id: driverId,
      ...(cardAssignmentId !== null && cardAssignmentId !== undefined
        ? { card_assignment_id: cardAssignmentId }
        : {}),
      tc_id: Number(values.tc_id),
      spending_type_id: Number(values.spending_type_id),
      date: values.date.format("YYYY-MM-DD"),
      currency: values.currency.toUpperCase() as CurrencyTypeUppercase,
    };
    const actionText = edit ? "updated" : "created";
    setIsLoading(true);

    try {
      let result: Response;
      if (edit) {
        const expenseToUpdate = expense as DetermineExpense<true>;
        if (!expenseToUpdate) {
          addMessage(
            "Expense could not be updated due to internal error",
            "error"
          );
          return;
        }
        result = await store.updateExpense(expenseToUpdate.id, parameters);
      } else {
        result = await store.createExpense(parameters);
      }

      const data: FleetExpenseResponseData = await result.json();
      if (onResult) {
        onResult(data);
      }
      addMessage(`Expense ${actionText} successfully`, "success");
    } catch {
      addMessage(
        `Expense could not be ${actionText} due to internal error`,
        "error"
      );
      if (onResult) {
        onResult(null);
      }
    } finally {
      callMessages();
      handler.executeClose();
      setIsLoading(false);
    }
  }, [
    cardAssignmentOptions,
    driverOptions,
    edit,
    expense,
    form,
    handler,
    onResult,
    store,
  ]);

  const cardAssignmentFieldsMap = useMemo<
    ConditionalFormSectionMap<FleetExpenseFormValues, "payment_type">
  >(() => {
    const fieldsMap: ConditionalFormSectionMap<
      FleetExpenseFormValues,
      "payment_type"
    > = new Map();
    fieldsMap.set(
      "By Card",
      <CardAssignmentFormSection
        drivers={driverOptions}
        form={form}
        onAssignmentsChange={setCardAssignments}
        onLoadingChange={setIsLoadingAssignments}
        refreshKey={assignmentRefreshKey}
        store={store}
      />
    );
    return fieldsMap;
  }, [assignmentRefreshKey, driverOptions, form, store]);

  const ModalFooter = () => (
    <>
      <Button key="cancel" onClick={handler.executeClose}>
        Cancel
      </Button>
      <Button
        key="create"
        type="primary"
        loading={isLoading}
        disabled={
          isLoadingAssignments ||
          driversLoading ||
          Boolean(driversError) ||
          !driverOptions ||
          driverOptions.getOptions().length === 0
        }
        onClick={handleSubmit}
      >
        {edit ? "Update" : "Create"} Expense
      </Button>
    </>
  );

  return (
    <ExecutableModal
      handler={handler}
      title={`${edit ? "Update" : "Create"} Expense`}
      footer={<ModalFooter />}
      onShow={onShow}
    >
      <Form form={form} layout="vertical">
        <DriverSelect
          error={driversError}
          form={form}
          isLoading={driversLoading}
          options={driverOptions}
        />
        <FleetExpenseItem name="tc_id" label="TC">
          <Select
            placeholder="Select TC"
            options={transports?.getSelectOptions() || []}
            disabled={!transportsLoading && !transports}
            loading={transportsLoading}
            {...searchByLabelProps}
          />
        </FleetExpenseItem>
        <FleetExpenseItem name="spending_type_id" label="Spending Type">
          <Select
            placeholder="Select Spending Type"
            options={spendingTypes?.getSelectOptions() || []}
            disabled={!spendingTypesLoading && !spendingTypes}
            loading={spendingTypesLoading}
            {...searchByLabelProps}
          />
        </FleetExpenseItem>
        <FleetExpenseItem name="date" label="Date">
          <DatePicker
            style={{ width: "100%" }}
            placeholder="Select date"
            allowClear={false}
          />
        </FleetExpenseItem>
        <FleetExpenseItem name="payment_type" label="Payment Type">
          <Select options={fleetExpensePaymentTypeMap.getSelectOptions()} />
        </FleetExpenseItem>
        <ConditionalFormSection
          key={assignmentRefreshKey}
          field="payment_type"
          map={cardAssignmentFieldsMap}
        />
        <FleetExpenseItem name="amount" label="amount">
          <Input type="number" placeholder="Enter amount" />
        </FleetExpenseItem>
        <FleetExpenseItem name="currency" label="Currency">
          <Select options={currencyMap.getSelectOptions()} />
        </FleetExpenseItem>
        <FleetExpenseItem name="note" label="Notes">
          <TextArea placeholder="Notes" rows={3} />
        </FleetExpenseItem>
      </Form>
    </ExecutableModal>
  );
};

export default FleetExpenseModal;
